import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for managing action history with undo/redo functionality
 * @param {*} initialState - Initial state value
 * @param {number} maxHistorySize - Maximum number of history entries to keep
 * @returns {Object} History management functions and state
 */
export const useActionHistory = (initialState, maxHistorySize = 50) => {
  const [currentState, setCurrentState] = useState(initialState);
  const [history, setHistory] = useState([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isUndoRedoOperation = useRef(false);

  /**
   * Add a new state to history
   * @param {*} newState - New state to add
   * @param {string} actionType - Type of action for debugging
   */
  const pushState = useCallback((newState, actionType = 'unknown') => {
    // Don't add to history if this is an undo/redo operation
    if (isUndoRedoOperation.current) {
      isUndoRedoOperation.current = false;
      return;
    }

    setHistory(prevHistory => {
      // Remove any future history if we're not at the end
      const newHistory = prevHistory.slice(0, currentIndex + 1);
      
      // Add the new state
      newHistory.push({
        state: newState,
        actionType,
        timestamp: Date.now()
      });

      // Limit history size
      if (newHistory.length > maxHistorySize) {
        return newHistory.slice(-maxHistorySize);
      }

      return newHistory;
    });

    setCurrentIndex(prevIndex => {
      const newIndex = Math.min(prevIndex + 1, maxHistorySize - 1);
      return newIndex;
    });

    setCurrentState(newState);
  }, [currentIndex, maxHistorySize]);

  /**
   * Undo the last action
   * @returns {boolean} True if undo was successful
   */
  const undo = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const previousState = history[newIndex];
      
      isUndoRedoOperation.current = true;
      setCurrentIndex(newIndex);
      setCurrentState(previousState.state || previousState);
      
      return true;
    }
    return false;
  }, [currentIndex, history]);

  /**
   * Redo the next action
   * @returns {boolean} True if redo was successful
   */
  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      const newIndex = currentIndex + 1;
      const nextState = history[newIndex];
      
      isUndoRedoOperation.current = true;
      setCurrentIndex(newIndex);
      setCurrentState(nextState.state || nextState);
      
      return true;
    }
    return false;
  }, [currentIndex, history]);

  /**
   * Clear all history and reset to initial state
   */
  const clearHistory = useCallback(() => {
    setHistory([initialState]);
    setCurrentIndex(0);
    setCurrentState(initialState);
  }, [initialState]);

  /**
   * Get information about the current history state
   * @returns {Object} History information
   */
  const getHistoryInfo = useCallback(() => {
    const currentEntry = history[currentIndex];
    const previousEntry = currentIndex > 0 ? history[currentIndex - 1] : null;
    const nextEntry = currentIndex < history.length - 1 ? history[currentIndex + 1] : null;

    return {
      canUndo: currentIndex > 0,
      canRedo: currentIndex < history.length - 1,
      currentActionType: currentEntry?.actionType || 'initial',
      previousActionType: previousEntry?.actionType || null,
      nextActionType: nextEntry?.actionType || null,
      historyLength: history.length,
      currentIndex,
      totalActions: history.length - 1 // Subtract initial state
    };
  }, [history, currentIndex]);

  /**
   * Replace current state without adding to history
   * Used for real-time updates during dragging
   * @param {*} newState - New state to set
   */
  const replaceCurrentState = useCallback((newState) => {
    setCurrentState(newState);
  }, []);

  /**
   * Commit the current state to history
   * Used after completing a drag operation
   * @param {string} actionType - Type of action
   */
  const commitCurrentState = useCallback((actionType = 'commit') => {
    pushState(currentState, actionType);
  }, [currentState, pushState]);

  return {
    // Current state
    currentState,
    
    // History management
    pushState,
    replaceCurrentState,
    commitCurrentState,
    
    // Undo/Redo operations
    undo,
    redo,
    clearHistory,
    
    // History information
    getHistoryInfo,
    
    // Convenience getters
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    historyLength: history.length
  };
};

/**
 * Hook specifically for managing dropped items history
 * @param {Array} initialItems - Initial items array
 * @returns {Object} Items history management
 */
export const useItemsHistory = (initialItems = []) => {
  const {
    currentState: items,
    pushState,
    replaceCurrentState,
    commitCurrentState,
    undo,
    redo,
    clearHistory,
    getHistoryInfo,
    canUndo,
    canRedo
  } = useActionHistory(initialItems);

  /**
   * Update items with history tracking
   * @param {Array} newItems - New items array
   * @param {string} actionType - Type of action
   */
  const updateItems = useCallback((newItems, actionType = 'update') => {
    pushState(newItems, actionType);
  }, [pushState]);

  /**
   * Update items without history (for real-time updates)
   * @param {Array} newItems - New items array
   */
  const updateItemsRealtime = useCallback((newItems) => {
    replaceCurrentState(newItems);
  }, [replaceCurrentState]);

  /**
   * Commit current items state to history
   * @param {string} actionType - Type of action
   */
  const commitItems = useCallback((actionType = 'position') => {
    commitCurrentState(actionType);
  }, [commitCurrentState]);

  return {
    items,
    updateItems,
    updateItemsRealtime,
    commitItems,
    undo,
    redo,
    clearHistory,
    getHistoryInfo,
    canUndo,
    canRedo
  };
};

export default useActionHistory;